/* @generated — do not edit directly */
// Generated by packages/schemas/scripts/generate-tool-metadata.ts

export type KnownIntegrationToolMetadata = { requiresPermission?: true };

export type KnownIntegrationMetadata = {
  key: string;
  name: string;
  description: string;
  logoUrl: string | null;
  image: string | null;
  docsUrl: string | null;
  connectionUi: string | null;
  nangoProviderConfigKey: string | null;
  useNangoMcp: boolean;
  usageGuidance: string | null;
  tools: Record<string, KnownIntegrationToolMetadata>;
};

export const knownIntegrationKeys = [
  "airops",
  "amplitude",
  "attentive",
  "basin",
  "clarity",
  "contentful",
  "contentsquare",
  "customer-io",
  "figma",
  "gethookd",
  "google-ads",
  "google-analytics",
  "google-calendar",
  "google-drive",
  "google-search-console",
  "gorgias",
  "intelligems",
  "judgeme",
  "junip",
  "klaviyo",
  "levanta",
  "linear",
  "loop-returns",
  "loop-subscriptions",
  "loox",
  "meta-marketing-api",
  "northbeam",
  "notion",
  "okendo",
  "opus",
  "ordergroove",
  "parallel",
  "pinterest",
  "posthog",
  "postscript",
  "rebuy",
  "recharge",
  "replo-assets",
  "replo-integrations",
  "replo-products",
  "reviews-io",
  "segment",
  "semrush",
  "shopify",
  "skio",
  "slack",
  "smile-io",
  "snapchat-ads",
  "stamped",
  "statsig",
  "stay-ai",
  "stripe",
  "trendtrack",
  "triple-whale",
  "vwo",
  "yotpo",
] as const;

export type KnownIntegrationKey = (typeof knownIntegrationKeys)[number];

const knownIntegrationKeySet: ReadonlySet<string> = new Set(
  knownIntegrationKeys,
);

export function isKnownIntegrationKey(
  value: string,
): value is KnownIntegrationKey {
  return knownIntegrationKeySet.has(value);
}

export const knownIntegrationToolMetadataRegistry = {
  airops: {
    key: "airops",
    name: "AirOps",
    description:
      "AirOps is a content platform for SEO and AEO (answer engine optimization), used to win citations across Google and answer engines like ChatGPT and Perplexity. Connect it to run your published apps and workflows and pull generated copy, SEO metadata, and product descriptions into Replo.",
    logoUrl: "/images/integration/logos/airops.png",
    image: "/images/integration/logos/airops.png",
    docsUrl: "https://docs.airops.com/api-reference",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'AirOps is an AI growth platform for content, SEO, and AEO (answer engine optimization): teams build no-code workflows that research, generate, optimize, and publish content for both Google and AI answer engines like ChatGPT, Perplexity, and Gemini. This integration runs a customer\'s published AirOps apps/workflows and reads their outputs (AI content, SEO copy, metadata, product descriptions) so results flow into Replo. Use when the user wants to run one of their AirOps apps/workflows and bring the result into Replo. Tool names: apps.execute (run synchronously), apps.executeAsync (queue long workflows), executions.get (poll an async run by execution UUID), executions.list (review past runs by numeric app id). AirOps has no app-discovery endpoint. If the user provides an app UUID, pass it directly to apps.execute or apps.executeAsync. Otherwise call show_integration_status and inspect connections[].connectionConfig.defaultAppUuid: omit appUuid when a default exists, and ask for the app UUID (found in AirOps under App -> Integrate) only when no default is stored. Never send a user who already provided an app UUID to integration settings; offer to save it as the default only if they want future runs to reuse it. Running an app consumes AirOps credits and may have side effects, so confirm with the user first. Common phrases: "run my AirOps workflow", "generate copy with AirOps", "run my SEO content app", "AEO content".',
    tools: {
      apps_execute: { requiresPermission: true },
      apps_execute_async: { requiresPermission: true },
      executions_get: {},
      executions_list: {},
    },
  },
  amplitude: {
    key: "amplitude",
    name: "Amplitude",
    description:
      "Amplitude is a digital product analytics platform for understanding user behavior. Read-only access to event segmentation, conversion funnels, retention, and active/new user counts across web and app.",
    logoUrl: "/images/integration/logos/amplitude.png",
    image: "/images/integration/logos/amplitude.png",
    docsUrl: null,
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "amplitude",
    useNangoMcp: true,
    usageGuidance:
      'Amplitude is a product analytics platform that tracks how users move through a product (events, funnels, retention, cohorts). Use when the user asks about Amplitude product analytics such as event counts, conversion funnels, retention, or active/new users. First call amplitude_events-list to discover valid event names, then call amplitude_event-segmentation, amplitude_funnel-analysis, amplitude_retention-analysis, or amplitude_user-counts with a bounded YYYYMMDD date range. Common phrases: "Amplitude", "event counts", "conversion funnel", "retention", "active users". Read-only.',
    tools: {
      event_segmentation: {},
      events_list: {},
      funnel_analysis: {},
      retention_analysis: {},
      user_counts: {},
    },
  },
  attentive: {
    key: "attentive",
    name: "Attentive",
    description:
      "Attentive is a personalized SMS and email marketing platform for ecommerce. Subscribe users, fire custom marketing events and journeys, track ecommerce activity (product views, add-to-cart, purchases), and manage subscriber attributes.",
    logoUrl: "/images/integration/logos/attentive.png",
    image: "/images/integration/logos/attentive.png",
    docsUrl: "https://docs.attentivemobile.com/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Attentive is a personalized SMS and email marketing platform for ecommerce brands, used to grow subscriber lists and trigger automated journeys. Use when the user wants to manage SMS/email subscribers, fire marketing events, or track ecommerce activity in Attentive. Common phrases: "subscribe this user", "send an event to Attentive", "track this purchase", "check if they\'re subscribed", "set subscriber attributes". Tools: attentive_subscribers.check (check subscription status by phone/email), attentive_subscribers.subscribe (opt-in a user; provide signUpSourceId from Attentive Sign-up Units tab, OR provide locale + subscriptionType instead), attentive_events.create (fire custom event to trigger journeys), attentive_attributes.set (set key-value attributes on a subscriber), attentive_ecommerce.productView / attentive_ecommerce.addToCart / attentive_ecommerce.purchase (track ecommerce events). Phone numbers must be E.164 format (e.g. +13115552368).',
    tools: {
      attributes_set: { requiresPermission: true },
      ecommerce_add_to_cart: { requiresPermission: true },
      ecommerce_product_view: {},
      ecommerce_purchase: { requiresPermission: true },
      events_create: { requiresPermission: true },
      subscribers_check: {},
      subscribers_subscribe: { requiresPermission: true },
    },
  },
  basin: {
    key: "basin",
    name: "Basin",
    description:
      "Basin (Usebasin) is a no-code form backend. Site forms post submissions to Basin, which stores them and exposes them via a REST API. Read and manage forms, submissions, projects, and sending domains.",
    logoUrl: "/images/integration/logos/basin.svg",
    image: "/images/integration/logos/basin.svg",
    docsUrl: "https://docs.usebasin.com/account-management/api-access/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Basin (Usebasin) is a no-code form backend that receives HTML form submissions and stores them. A site form posts directly to a Basin endpoint (https://usebasin.com/f/FORM_ID); Basin captures the submission and this integration reads and manages that data.\n\nUse when the user wants to inspect form submissions ("show my latest contact form leads", "any new submissions?"), manage their Basin forms ("create a form for my waitlist", "what\'s my form endpoint?"), or organize projects and sending domains.\n\nNotes: Basin\'s API cannot create submissions (visitors submit directly to the form endpoint) or delete forms/submissions. Domains are read-only (list). To capture submissions on a Replo site, point a form\'s action at the Basin form endpoint.',
    tools: {
      domains_list: {},
      forms_create: { requiresPermission: true },
      forms_get: {},
      forms_list: {},
      forms_update: { requiresPermission: true },
      projects_create: { requiresPermission: true },
      projects_get: {},
      projects_list: {},
      projects_update: { requiresPermission: true },
      submissions_get: {},
      submissions_list: {},
      submissions_update: { requiresPermission: true },
    },
  },
  clarity: {
    key: "clarity",
    name: "Microsoft Clarity",
    description:
      "Microsoft Clarity is a free website behavior analytics tool. Query dashboard metrics, session recordings, heatmaps, and engagement signals like dead clicks, rage clicks, and scroll depth.",
    logoUrl: "/images/integration/logos/microsoftclarity.png",
    image: "/images/integration/logos/microsoftclarity.png",
    docsUrl:
      "https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-data-export-api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Microsoft Clarity is a free website behavior analytics product covering heatmaps, session recordings, and engagement metrics. Use these tools when the user asks about website analytics, user behavior, heatmap data, session insights, engagement metrics, or behavioral analytics.\n\nAvailable tools:\n- clarity.query_dashboard: Ask natural language questions about Clarity analytics data. Examples: "How many sessions from mobile in the last 3 days?", "What\'s the dead click rate on the homepage?", "Show traffic by country". This is the most flexible tool, so use it first.\n- clarity.list_recordings: List session recordings filtered by date range, URLs, devices, browsers, OS, country, city. Useful for finding sessions with specific behavior (rage clicks, JS errors, high click count). Returns session metadata and playback URLs.\n- clarity.get_insights: Structured data export with rigid dimensions (Browser, Device, OS, Source, Campaign, Channel, URL). Use when you need structured/tabular data rather than a natural language answer. Limited to last 1-3 days, 10 requests/day.\n\nExample triggers: "how is my site performing", "show me user behavior data", "what are my rage click rates", "where are users dropping off", "show me traffic by device", "find sessions with errors", "list mobile recordings from yesterday"',
    tools: {
      clarity_get_insights: {},
      clarity_list_recordings: {},
      clarity_query_dashboard: {},
    },
  },
  contentful: {
    key: "contentful",
    name: "Contentful",
    description:
      "Contentful is a headless CMS. Browse and edit spaces, content types (collections), entries, and assets, and render Contentful content on pages.",
    logoUrl: "/images/integration/logos/contentful.svg",
    image: "/images/integration/logos/contentful.svg",
    docsUrl: "https://docs.replo.app/integrations/contentful",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "contentful",
    useNangoMcp: true,
    usageGuidance:
      'Contentful is a headless CMS. In Contentful a "content type" is the schema for a collection of entries, and an "entry" is one item of content. Use when the user wants to browse, create, update, or publish Contentful content, or render Contentful content on pages. Common phrases: "show my Contentful content types", "add these recipes to Contentful", "publish this entry", "build a blog page from Contentful". Chat tools use the Management API (including drafts); page rendering uses Delivery API loaders (published only) and needs a Delivery API token per space in Integrations → Contentful. Load the contentful skill for tool workflow and page-loader patterns.',
    tools: {
      create_asset_from_url: { requiresPermission: true },
      create_entry: { requiresPermission: true },
      get_asset: {},
      get_content_type: {},
      get_entry: {},
      list_assets: {},
      list_content_types: {},
      list_entries: {},
      list_environments: {},
      list_locales: {},
      list_spaces: {},
      publish_asset: { requiresPermission: true },
      publish_entry: { requiresPermission: true },
      unpublish_entry: { requiresPermission: true },
      update_entry: { requiresPermission: true },
    },
  },
  contentsquare: {
    key: "contentsquare",
    name: "Contentsquare",
    description:
      "Contentsquare is a digital experience analytics platform. Read-only access to the Metrics API: site and page-group metrics (visits, bounce rate, conversion rate, revenue, scroll rate, session time, web vitals), zone metrics, and the segments, goals, mappings, page groups, and zonings that scope them.",
    logoUrl: "/images/integration/logos/contentsquare.svg",
    image: "/images/integration/logos/contentsquare.svg",
    docsUrl: "https://docs.contentsquare.com/en/api/metrics/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Contentsquare (CS) is a digital experience / CRO analytics platform. Use when the user asks about their Contentsquare data: site or page-level visits, bounce rate, conversion rate, revenue, scroll rate, session time, web vitals, or in-page zone engagement. Common phrases: "Contentsquare", "how is my landing page performing", "bounce rate", "scroll rate", "conversion rate by page", "zone clicks". Read-only.\n\nDiscovery-then-query flow: metric tools that target a page group or zone need an ID first.\n- listSegments / listGoals — segment and goal IDs used to filter metrics (segmentIds, goalId).\n- listMappings — a mapping models the site; get a mapping ID, then listPageGroups(mappingId) for its page groups.\n- listZonings(pageGroupId) — zonings for a page group; then listZones(zoningId) for its zones.\n- getSiteMetrics(startDate, endDate) — whole-site metrics. Start here for site-wide questions.\n- getPageGroupMetrics(pageGroupId, startDate, endDate) — metrics for one page group.\n- getZoneMetrics(zoneId, startDate, endDate) — in-page zone metrics (requires the Zoning Analysis tier).\n\nAll dates are YYYY-MM-DD (UTC). segmentIds/goalId/device/period are optional filters on the metric tools. Load the contentsquare skill before calling the metric tools — it covers the mapping → page group → zoning → zone hierarchy and the metric vocabulary.',
    tools: {
      get_page_group_metrics: {},
      get_site_metrics: {},
      get_zone_metrics: {},
      list_goals: {},
      list_mappings: {},
      list_page_groups: {},
      list_segments: {},
      list_zones: {},
      list_zonings: {},
    },
  },
  "customer-io": {
    key: "customer-io",
    name: "Customer.io",
    description:
      "Customer.io is a customer engagement platform for lifecycle and behavioral messaging across email, SMS, and push. Read campaigns, broadcasts, and segments, look up people, track behavioral events, and send transactional emails or trigger broadcasts.",
    logoUrl: "/images/integration/logos/customer-io.svg",
    image: "/images/integration/logos/customer-io.svg",
    docsUrl: "https://customer.io/docs/api/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Customer.io is a customer engagement platform for automated, behavior-driven messaging across email, SMS, and push. Use when the user wants to inspect or act on their Customer.io messaging workspace: list campaigns/broadcasts/segments, look up a person, track an event, identify/update a person, send a transactional email, or trigger a broadcast. Tool names: customer_io_campaigns_list, customer_io_campaigns_get, customer_io_broadcasts_list, customer_io_segments_list, customer_io_segments_get_customer_count, customer_io_customers_search, customer_io_customers_get, customer_io_people_identify, customer_io_events_track, customer_io_messages_send_transactional, customer_io_campaigns_trigger. Common phrases: "list my Customer.io campaigns", "how big is the VIP segment", "find the person with email ...", "track a purchase event for ...", "identify this customer", "send a transactional email", "trigger the welcome broadcast". IMPORTANT: customer_io_messages_send_transactional sends a real email and customer_io_campaigns_trigger sends a broadcast to real recipients, so always confirm with the user before calling either. customer_io_people_identify and customer_io_events_track require Track API credentials (Site ID + Track API Key) to be configured on the connection.',
    tools: {
      broadcasts_list: {},
      campaigns_get: {},
      campaigns_list: {},
      campaigns_trigger: { requiresPermission: true },
      customers_get: {},
      customers_search: {},
      events_track: { requiresPermission: true },
      messages_send_transactional: { requiresPermission: true },
      people_identify: { requiresPermission: true },
      segments_get_customer_count: {},
      segments_list: {},
    },
  },
  figma: {
    key: "figma",
    name: "Figma",
    description:
      "Read Figma design files, nodes, components, styles, comments, and dev resources, and post comments back to a file.",
    logoUrl: "/images/integration/logos/figma.svg",
    image: "/images/integration/logos/figma.svg",
    docsUrl: "https://nango.dev/docs/integrations/all/figma",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "figma",
    useNangoMcp: true,
    usageGuidance:
      'Figma is a design tool for UI/UX mockups and design systems. Use when the user wants to pull design context from Figma: inspect a file\'s structure or specific nodes, render nodes as images, list components and styles, browse a team\'s projects and files, or read and post comments. Common phrases: "from Figma", "my Figma design", "this Figma file", "Figma mockup", "the Figma". Saved Figma team/project IDs may already be in project memory — check there before asking the user for a link. Load the figma skill for how to find a file and use the tools.',
    tools: {
      create_file_comment: { requiresPermission: true },
      get_component: {},
      get_file: {},
      get_file_nodes: {},
      get_images: {},
      get_style: {},
      list_file_comments: {},
      list_file_components: {},
      list_file_dev_resources: {},
      list_file_styles: {},
    },
  },
  gethookd: {
    key: "gethookd",
    name: "GetHookd",
    description:
      "GetHookd is an ad intelligence platform. Search Meta ads, look up brands, pull Brand Spy top ads, and browse swipe-file boards for creative inspiration.",
    logoUrl: "/images/integration/logos/gethookd.svg",
    image: "/images/integration/logos/gethookd.svg",
    docsUrl: "https://www.gethookd.ai/api/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'GetHookd is a Meta ad library and creative intelligence tool. Use it when the user wants competitor ad research, winning hooks/CTAs, brand spy top ads, or swipe-file boards.\n\nIMPORTANT: gethookd.exploreAds consumes paid GetHookd credits (about 0.01 per result). Always confirm with the user before calling it, or check remaining access with gethookd.authCheck first. gethookd.getAd, gethookd.searchBrands, gethookd.brandSpyTopAds, gethookd.listBoards, and gethookd.authCheck are free.\n\nExample triggers: "find winning ads in skincare", "what ads is brand X running", "show top Brand Spy ads", "search GetHookd for funnel ads", "how many GetHookd credits do I have".',
    tools: {
      gethookd_auth_check: {},
      gethookd_brand_spy_top_ads: {},
      gethookd_explore_ads: { requiresPermission: true },
      gethookd_get_ad: {},
      gethookd_list_boards: {},
      gethookd_search_brands: {},
    },
  },
  "google-ads": {
    key: "google-ads",
    name: "Google Ads",
    description:
      "Google Ads is Google's advertising platform for Search, Shopping, Display, and video. Read and manage campaigns, ad groups, keywords, and performance data, and launch or edit Search campaigns via mutate actions.",
    logoUrl: "/images/integration/logos/google-ads.svg",
    image: "/images/integration/logos/google-ads.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "google-ads",
    useNangoMcp: true,
    usageGuidance:
      'Google Ads is Google\'s pay-per-click advertising platform spanning Search, Shopping, Display, and video. Use when the user wants to inspect or manage their Google Ads account: list accessible customer accounts, view campaigns/ad groups/keywords/ads/budgets, pull performance metrics (impressions, clicks, cost, CTR, CPC, ROAS, conversions), or launch and edit Search campaigns. Common phrases: "Google Ads", "AdWords", "my campaigns", "ad spend", "ad performance", "launch a campaign", "start a search campaign", "pause campaign", "add keywords", "create an ad". Read tools: `list-accessible-customers` (call first to discover the customerId), `search` (runs any GAQL query). Mutate tools: `create-campaign-budget`, `create-campaign`, `add-campaign-geo-targets`, `create-ad-group`, `add-keywords`, `create-responsive-search-ad`, `update-campaign-status`. Load the google-ads skill for the required multi-step launch flow, GAQL examples, and failure handling before creating or editing any campaign.',
    tools: {
      add_campaign_geo_targets: { requiresPermission: true },
      add_keywords: { requiresPermission: true },
      create_ad_group: { requiresPermission: true },
      create_campaign: { requiresPermission: true },
      create_campaign_budget: { requiresPermission: true },
      create_responsive_search_ad: { requiresPermission: true },
      list_accessible_customers: {},
      search: {},
      update_campaign_status: { requiresPermission: true },
    },
  },
  "google-analytics": {
    key: "google-analytics",
    name: "Google Analytics",
    description:
      "Google Analytics 4 (GA4) is Google's web and app analytics platform. Read-only access to properties, reports, and realtime data covering traffic, page views, active users, sources, and conversions.",
    logoUrl: "/images/integration/logos/google-analytics.svg",
    image: "/images/integration/logos/google-analytics.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "google-analytics",
    useNangoMcp: true,
    usageGuidance:
      'Google Analytics 4 (GA4) is Google\'s website and app analytics platform. Use when the user wants to inspect GA4 data: traffic, page views, active users, realtime activity, top pages, sources, or conversions. Common phrases: "Google Analytics", "GA4", "how many visitors", "page views", "traffic", "active users", "realtime users", "top pages", "conversions last week". Read-only. Load the google-analytics skill for tool list, recommended call order, and metric/dimension guidance.',
    tools: {
      get_property_metadata: {},
      list_conversion_events: {},
      list_properties: {},
      run_realtime_report: {},
      run_report: {},
    },
  },
  "google-calendar": {
    key: "google-calendar",
    name: "Google Calendar",
    description:
      "Google Calendar is Google's scheduling and calendar service. Create, update, delete, and list events, including timed, all-day, and recurring events, and manage attendees.",
    logoUrl: "/images/integration/logos/googlecalendar.svg",
    image: "/images/integration/logos/googlecalendar.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "google-calendar",
    useNangoMcp: true,
    usageGuidance:
      'Google Calendar is Google\'s scheduling and calendar service. Use when the user wants to schedule meetings or appointments, set reminders, block time on their calendar, create/update/delete calendar events, or list upcoming events. Common phrases: "schedule", "meeting", "calendar", "remind me at", "add to my calendar", "block time", "all-day event", "reschedule", "move meeting". Use google_calendar_create_event for timed local-time scheduling and google_calendar_create_all_day_event for all-day events. Load the google-calendar skill for detailed tool guidance.',
    tools: {
      add_attendee: { requiresPermission: true },
      create_all_day_event: { requiresPermission: true },
      create_event: { requiresPermission: true },
      create_recurring_event: { requiresPermission: true },
      delete_event: { requiresPermission: true },
      find_free_slots: {},
      get_event: {},
      get_event_instances: {},
      list_events: {},
      list_settings: {},
      list_upcoming_events: {},
      move_event: { requiresPermission: true },
      patch_event: { requiresPermission: true },
      query_free_busy: {},
      quick_add_event: { requiresPermission: true },
      remove_attendee: { requiresPermission: true },
      search_events: {},
      update_attendee_response: { requiresPermission: true },
      update_event: { requiresPermission: true },
    },
  },
  "google-drive": {
    key: "google-drive",
    name: "Google Drive",
    description:
      "Google Drive is Google's cloud file storage service. Create, read, update, move, and delete files and folders, and search across a user's Drive.",
    logoUrl: "/images/integration/logos/googledrive.svg",
    image: "/images/integration/logos/googledrive.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "google-drive",
    useNangoMcp: true,
    usageGuidance:
      'Google Drive is Google\'s cloud file storage service. Use when the user wants to upload files to Google Drive, list or search for files, update file metadata, move files between folders, create folders, or delete files. Common phrases: "upload to Drive", "find file", "list my Drive files", "create folder", "move file", "delete file", "Google Drive". Load the google-drive skill for detailed tool guidance.',
    tools: {
      create_folder: { requiresPermission: true },
      delete_file: { requiresPermission: true },
      download_file: {},
      find_file: {},
      find_folder: {},
      get_file_metadata: {},
      list_files_non_unified: {},
      list_shared_drives: {},
      move_file: { requiresPermission: true },
      read_file_content: {},
      update_file: { requiresPermission: true },
      upload_document: { requiresPermission: true },
    },
  },
  "google-search-console": {
    key: "google-search-console",
    name: "Google Search Console",
    description:
      "Google Search Console (GSC) is Google's free SEO tool for monitoring organic Search presence. Read properties, search performance (clicks, impressions, CTR, position), sitemaps, and URL indexing status, and submit sitemaps when approved.",
    logoUrl: "/images/integration/logos/google-search-console.svg",
    image: "/images/integration/logos/google-search-console.svg",
    docsUrl: "https://nango.dev/docs/api-integrations/google-search-console",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "google-search-console",
    useNangoMcp: true,
    usageGuidance:
      'Google Search Console (GSC) is Google\'s free tool for monitoring a site\'s organic presence in Google Search. Use when the user wants to inspect organic Google Search performance, SEO queries/pages, indexing status, sitemap health, rich results, or Search Console properties. Common phrases: "Google Search Console", "GSC", "SEO traffic", "search queries", "indexed status", "submit sitemap", "sitemaps", "average position", "CTR", "rich results". Read tools: `list_sites` (call first to discover siteUrl), `query_search_analytics` (clicks, impressions, CTR, position by query/page/device/country/date/searchAppearance), `list_sitemaps`, `inspect_url` (index status and rich results). Mutate tools: `submit_sitemap`; confirm with the user before calling.',
    tools: {
      inspect_url: {},
      list_sitemaps: {},
      list_sites: {},
      query_search_analytics: {},
      submit_sitemap: { requiresPermission: true },
    },
  },
  gorgias: {
    key: "gorgias",
    name: "Gorgias",
    description:
      "Gorgias is a customer support helpdesk for e-commerce brands. Read support tickets, conversations, customers, satisfaction surveys, and tags.",
    logoUrl: "/images/integration/logos/gorgias.svg",
    image: "/images/integration/logos/gorgias.svg",
    docsUrl: "https://developers.gorgias.com/reference/introduction",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Gorgias is a customer support helpdesk (tickets, live chat, email, and voice) used by e-commerce brands. Use these tools when the user asks about support tickets, customer conversations, complaints, common support issues or themes, CSAT/satisfaction scores, or a specific customer\'s support history. Common phrases: "what are customers complaining about", "show recent support tickets", "what\'s our CSAT", "find tickets about [topic]", "support history for [customer/email]".\n\nAvailable tools:\n- tickets.list: List support tickets, optionally filtered by customer ID or view ID, ordered by created/updated date, paginated.\n- tickets.get: Get one ticket by ID (subject, status, customer, assignee, tags, AI summary).\n- messages.list: List the messages of a ticket (or recent messages across tickets) to read the actual conversation.\n- customers.list: List customers, paginated, ordered by name.\n- customers.get: Get one customer by ID.\n- resources.search: Search customers (by name/email), agents, teams, or tags by free text. Use this to find a customer\'s ID before calling tickets.list with customerId.\n- surveys.list: List satisfaction survey responses (CSAT scores and feedback text).\n- tags.list: List ticket tags, useful for summarizing common support themes.\n\nTo install the Gorgias chat bubble on the site, do not use these tools — add the Gorgias script from Scripts & Pixels in site settings instead (it only needs the chat app key from Gorgias chat settings → Installation).',
    tools: {
      customers_get: {},
      customers_list: {},
      messages_list: {},
      resources_search: {},
      surveys_list: {},
      tags_list: {},
      tickets_get: {},
      tickets_list: {},
    },
  },
  intelligems: {
    key: "intelligems",
    name: "Intelligems",
    description:
      "Intelligems is a profit-optimization and A/B testing platform for Shopify. Test prices, shipping, discounts, and content, then read per-variation and sitewide analytics like profit per visitor, conversion rate, and revenue.",
    logoUrl: "/images/integration/logos/intelligems.png",
    image: "/images/integration/logos/intelligems.png",
    docsUrl: "https://docs.intelligems.io/developer-resources/external-api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Intelligems is a profit-optimization and A/B testing platform for Shopify: brands test prices, shipping rates, discounts, offers, and site content, then measure profit per visitor. Use when the user wants to check A/B test results, view experiment analytics, create or manage price/shipping/content tests, get sitewide revenue and conversion metrics, or interact with Intelligems experiences. Available tools: intelligems_experiences.list (list experiences), intelligems_experiences.get (full experience details), intelligems_analytics.getExperience (per-variation results), intelligems_analytics.getSitewide (sitewide KPI snapshot), intelligems_experiences.create (create an experience), intelligems_experiences.update (rename / change attribution), intelligems_experiences.action (start/pause/end). Intelligems vocabulary is specific and does not match the words users say: status is pending/started/ended/paused (never draft or running), category is only experiment or personalization and is derived — when creating, pass "type" instead (pricing for a price test, shipping, content/onsiteEdits, personalization/offer, checkoutBlock, postPurchase, sitewide, content/url, content/theme, content/template, content/advanced). List tools paginate with page (1-indexed), not offset. Ending an experience requires the winning variationId from experiences.get. Common phrases: "how is my price test doing", "show test results", "create an A/B test", "pause the experiment", "sitewide analytics", "revenue per visitor", "conversion rate". IMPORTANT: Always confirm with the user before calling experiences.create, experiences.update, or experiences.action, since these change live tests.',
    tools: {
      analytics_get_experience: {},
      analytics_get_sitewide: {},
      experiences_action: { requiresPermission: true },
      experiences_create: { requiresPermission: true },
      experiences_get: {},
      experiences_list: {},
      experiences_update: { requiresPermission: true },
    },
  },
  judgeme: {
    key: "judgeme",
    name: "Judge.me",
    description:
      "Judge.me is a product reviews and ratings app for Shopify. List, search, and moderate reviews, view review counts and star ratings, and post public replies to reviews.",
    logoUrl: "/images/integration/logos/judgeme.png",
    image: "/images/integration/logos/judgeme.png",
    docsUrl: "https://judge.me/api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Judge.me is a widely used product reviews and ratings app for Shopify stores. Use when the user asks about product reviews, star ratings, review counts, or wants to moderate/reply to reviews. Common phrases: "show me reviews for", "what\'s the average rating", "how many reviews does X have", "reply to this review", "hide this review". Tools: judgeme_reviews_list (list/filter reviews by product or rating), judgeme_reviews_get (get single review), judgeme_reviews_count (get review count), judgeme_reviews_moderate (publish/hide a review; ALWAYS confirm with user first), judgeme_replies_create (post a public reply; ALWAYS confirm with user first). Note: productExternalId is the Shopify numeric product ID (not GID). Use shopify_products_search to find product IDs first if needed.',
    tools: {
      replies_create: { requiresPermission: true },
      reviews_count: {},
      reviews_get: {},
      reviews_list: {},
      reviews_moderate: { requiresPermission: true },
    },
  },
  junip: {
    key: "junip",
    name: "Junip",
    description:
      "Junip is a product reviews and ratings app for Shopify and ecommerce brands. Access product reviews, aggregate star ratings, and store-level reviews.",
    logoUrl: "/images/integration/logos/junip.png",
    image: "/images/integration/logos/junip.png",
    docsUrl: "https://junip.co/docs/api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Junip is a reviews and ratings platform for Shopify brands, used to collect and display product and store reviews. Use when the user asks about product reviews, star ratings, review counts, or customer feedback from Junip. Common phrases: "show reviews for this product", "what\'s the average rating", "how many reviews", "show store reviews". Tools: reviews.search (list product reviews by product ID with optional rating filter and sorting), reviews.getProductRating (get aggregate rating and count for a product), storeReviews.search (list store-level reviews).',
    tools: {
      reviews_get_product_rating: {},
      reviews_search: {},
      store_reviews_search: {},
    },
  },
  klaviyo: {
    key: "klaviyo",
    name: "Klaviyo",
    description:
      "Klaviyo is a B2C CRM and marketing platform unifying customer data with email, SMS, and reviews. Read-only access to account summary, customer profiles, lists, campaigns, signup forms, and reviews.",
    logoUrl: "/images/integration/logos/klaviyo.svg",
    image: "/images/integration/logos/klaviyo.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "klaviyo-oauth",
    useNangoMcp: true,
    usageGuidance:
      'Klaviyo is a B2C CRM and marketing automation platform that unifies customer data to power email, SMS, and reviews. Use when the user wants to view their Klaviyo account summary, list or search customer profiles, view lists, view campaigns, view signup forms, or browse product reviews. Available tools: get_account_summary, lists, campaigns, forms, profiles, reviews. Common phrases: "Klaviyo list", "email list", "subscriber", "marketing list", "email campaign", "signup form", "product reviews", "embed Klaviyo form", "connect my form to Klaviyo". IMPORTANT: Always load the klaviyo skill before wiring signup/capture UI or calling these tools.',
    tools: {
      campaigns: {},
      forms: {},
      get_account_summary: {},
      lists: {},
      profiles: {},
      reviews: {},
    },
  },
  levanta: {
    key: "levanta",
    name: "Levanta",
    description:
      "Levanta is an affiliate and creator marketing platform for ecommerce brands selling on Amazon, Walmart, and Shopify. Search affiliate products, create tracking links, list brand partnerships, and pull performance reports.",
    logoUrl: "/images/integration/logos/levanta.svg",
    image: "/images/integration/logos/levanta.svg",
    docsUrl: "https://api-docs.levanta.io",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Levanta is an affiliate and creator marketing platform for ecommerce brands, unifying programs across Amazon, Walmart, and Shopify. Use when the user wants to search for affiliate products on Amazon/Walmart, create affiliate tracking links, list brand partnerships, or view performance reports. Common phrases: "affiliate product", "tracking link", "commission", "Levanta", "affiliate link", "product search", "brand partnership", "performance report". Available tools: brands.list, products.search, products.get, links.create, links.list, reports.list. IMPORTANT: You MUST load the levanta skill before writing any page code that involves Levanta product data, since it contains the required data loading pattern.',
    tools: {
      brands_list: {},
      links_create: { requiresPermission: true },
      links_list: {},
      products_get: {},
      products_search: {},
      reports_list: {},
    },
  },
  linear: {
    key: "linear",
    name: "Linear",
    description:
      "Linear is an issue tracking and project management tool for software teams. Search, create, update, and manage issues, projects, teams, cycles, comments, labels, and attachments.",
    logoUrl: "/images/integration/logos/linear.svg",
    image: "/images/integration/logos/linear.svg",
    docsUrl: "https://nango.dev/docs/api-integrations/linear",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "linear",
    useNangoMcp: true,
    usageGuidance:
      "Linear is an issue tracking and project management tool for software and product teams. Use when the user wants to read or manage Linear issues, projects, teams, cycles, workflow states, users, comments, labels, issue relations, or attachments. Use search-issues when finding issues by text, list-teams before creating issues when the team is ambiguous, and list-projects when the project is ambiguous. Confirm with the user before calling tools that create, update, delete, archive, unarchive, add, remove, resolve, or unresolve Linear data unless the user explicitly asked for that exact mutation.",
    tools: {
      add_issue_label: { requiresPermission: true },
      archive_cycle: { requiresPermission: true },
      archive_issue: { requiresPermission: true },
      create_attachment: { requiresPermission: true },
      create_comment: { requiresPermission: true },
      create_cycle: { requiresPermission: true },
      create_issue: { requiresPermission: true },
      create_issue_relation: { requiresPermission: true },
      create_project: { requiresPermission: true },
      delete_attachment: { requiresPermission: true },
      delete_comment: { requiresPermission: true },
      delete_issue: { requiresPermission: true },
      delete_issue_relation: { requiresPermission: true },
      get_attachment: {},
      get_comment: {},
      get_cycle: {},
      get_issue: {},
      get_issue_label: {},
      get_project: {},
      get_team: {},
      get_user: {},
      get_viewer: {},
      get_workflow_state: {},
      list_attachments: {},
      list_comments: {},
      list_cycles: {},
      list_issue_labels: {},
      list_issues: {},
      list_projects: {},
      list_teams: {},
      list_users: {},
      list_workflow_states: {},
      remove_issue_label: { requiresPermission: true },
      resolve_comment: { requiresPermission: true },
      search_issues: {},
      unarchive_issue: { requiresPermission: true },
      unarchive_project: { requiresPermission: true },
      unresolve_comment: { requiresPermission: true },
      update_comment: { requiresPermission: true },
      update_cycle: { requiresPermission: true },
      update_issue: { requiresPermission: true },
      update_issue_relation: { requiresPermission: true },
      update_project: { requiresPermission: true },
    },
  },
  "loop-returns": {
    key: "loop-returns",
    name: "Loop Returns",
    description:
      "Loop Returns is a returns and exchanges platform for Shopify brands. Read return data and perform return actions like process, flag, close, cancel, and add notes.",
    logoUrl: "/images/integration/logos/loop-returns.png",
    image: "/images/integration/logos/loop-returns.png",
    docsUrl: "https://docs.loopreturns.com/api-reference",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Loop Returns is a post-purchase returns and exchanges platform for Shopify brands. Use when the user asks about returns, exchanges, RMAs, refunds, or return reasons, or wants to act on a return. Common phrases: "show recent returns", "why was order #1234 returned", "return details", "return notes", "process this return", "flag this return for review", "close this return", "cancel this return", "add a note to this return". Available tools: returns.list (list/filter returns by date range and state), returns.get (full return details by return ID, order ID, or order name), returns.getNotes (internal notes on a return), returns.process (finalize a return and trigger refunds/exchanges; confirm first), returns.flag (flag a return for manual review; confirm first), returns.close (close a return without fulfilling outcomes; confirm first), returns.cancel (cancel a return so items can be returned again; confirm first), returns.createNote (add an internal note to a return; confirm first).',
    tools: {
      returns_cancel: { requiresPermission: true },
      returns_close: { requiresPermission: true },
      returns_create_note: { requiresPermission: true },
      returns_flag: { requiresPermission: true },
      returns_get: {},
      returns_get_notes: {},
      returns_list: {},
      returns_process: { requiresPermission: true },
    },
  },
  "loop-subscriptions": {
    key: "loop-subscriptions",
    name: "Loop Subscriptions",
    description:
      "Loop Subscriptions is a subscription management app for Shopify. Read subscription contracts, billing schedules, delivery policies, and line items.",
    logoUrl: "/images/integration/logos/loop-subscriptions.png",
    image: "/images/integration/logos/loop-subscriptions.png",
    docsUrl: "https://developer.loopwork.co/reference",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Loop Subscriptions is a subscription and recurring-order management app for Shopify brands. Use when the user asks about subscriptions, recurring orders, billing schedules, subscription status, a specific customer\'s subscriptions, or past subscription orders. Common phrases: "show subscriptions", "list active subscriptions", "subscription details", "next billing date", "subscription status", "subscriptions for [customer/email]", "recent subscription orders", "orders this month". Available tools: subscriptions.list (list/filter subscriptions by status), subscriptions.get (get full subscription details by ID), subscriptions.listForCustomer (all subscriptions for one customer by Shopify customer ID or email, optionally filtered by status), orders.list (list past subscription orders, filterable by created/updated date range with pagination).',
    tools: {
      orders_list: {},
      subscriptions_get: {},
      subscriptions_list: {},
      subscriptions_list_for_customer: {},
    },
  },
  loox: {
    key: "loox",
    name: "Loox",
    description:
      "Loox is a photo and video product reviews app for Shopify. Access product reviews with visual UGC, store-level reviews, and star ratings.",
    logoUrl: "/images/integration/logos/loox.png",
    image: "/images/integration/logos/loox.png",
    docsUrl: "https://loox.io/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Loox is a visual reviews app for Shopify, known for photo and video product reviews. Use these tools when the user asks about product reviews, store reviews, ratings, customer feedback, or photo/video reviews from Loox.\n\nAvailable tools:\n- loox_reviews.list: List product reviews with full reviewer details. Filter by product (numeric Shopify product ID), rating, moderation status, and date range.\n- loox_reviews.get: Get a single product review by its ID.\n- loox_store_reviews.list: List store-level reviews (about the overall shopping experience, not a specific product).\n- loox_store_reviews.get: Get a single store review by its ID.\n\nWhen the user wants to display reviews, star ratings, or a rating breakdown on a page, load the "loox" skill for the correct loader component pattern so the data stays live instead of being baked in at build time.\n\nExample triggers: "show me Loox reviews for this product", "what are customers saying", "list our recent reviews", "show store reviews", "add a reviews section to the page", "show the star rating".',
    tools: {
      reviews_get: {},
      reviews_list: {},
      store_reviews_get: {},
      store_reviews_list: {},
    },
  },
  "meta-marketing-api": {
    key: "meta-marketing-api",
    name: "Meta Ads",
    description:
      "Meta Marketing API (Facebook & Instagram Ads). Read ad accounts, campaigns, ad sets, ads, and creatives, pull performance insights like spend, ROAS, CTR, and conversions, and build full campaigns (campaign, ad set, creative, ad).",
    logoUrl: "/images/integration/logos/meta-marketing-api.svg",
    image: "/images/integration/logos/meta-marketing-api.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "meta-marketing-api",
    useNangoMcp: true,
    usageGuidance:
      'Meta Marketing API exposes a merchant\'s Facebook & Instagram advertising data (Meta Ads Manager). Use when the user wants to inspect their Meta/Facebook/Instagram ad performance, pull their ad creatives in as context, or build a campaign. Read tools: `list-ad-accounts`, `list-campaigns`, `list-ad-sets`, `list-ads`, `list-ad-creatives`, `get-ad-video` (resolve a creative\'s video_id to a playable URL), `list-pages`, `get-insights`. Write tools (all need ads_management and start objects PAUSED): `create-campaign`, `create-ad-set`, `upload-ad-video`, `create-ad-creative`, `create-ad`. Discovery: call `list-ad-accounts` first for an `act_…` adAccountId. For metrics use `get-insights` with a `datePreset` (e.g. last_7d, last_30d) or a `timeRange`. To build a full campaign, go in order: `create-campaign` → `create-ad-set` (targeting + budget) → `create-ad-creative` (needs a `pageId` from `list-pages` and a live published landing-page URL as its `link`) → `create-ad` (pairs the ad set and creative). For a VIDEO ad, first `upload-ad-video` (by URL), poll `get-ad-video` until status is `ready`, then pass its `videoId` to `create-ad-creative`. Nothing spends until every layer exists and the user activates it. ALWAYS confirm the account, budgets (with dollar amounts), and destination with the user before any write. Common phrases: "Meta Ads", "Facebook Ads", "my ad spend", "ROAS", "ad performance", "my creatives", "create a campaign", "build an ad", "set up the campaign". Load the meta-ads skill before creating or building anything, or when the user asks for an Ads Manager link.',
    tools: {
      create_ad: { requiresPermission: true },
      create_ad_creative: { requiresPermission: true },
      create_ad_set: { requiresPermission: true },
      create_campaign: { requiresPermission: true },
      get_ad_video: {},
      get_insights: {},
      list_ad_accounts: {},
      list_ad_creatives: {},
      list_ad_sets: {},
      list_ads: {},
      list_campaigns: {},
      list_pages: {},
      upload_ad_video: { requiresPermission: true },
    },
  },
  northbeam: {
    key: "northbeam",
    name: "Northbeam",
    description:
      "Northbeam is a marketing attribution and analytics platform for ecommerce brands. Read performance metrics (spend, revenue, ROAS, CAC, AOV, transactions) across attribution models, windows, and cash/accrual accounting modes, broken down by channel, platform, or campaign.",
    logoUrl: "/images/integration/logos/northbeam.svg",
    image: "/images/integration/logos/northbeam.svg",
    docsUrl: "https://docs.northbeam.io/",
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "northbeam",
    useNangoMcp: true,
    usageGuidance:
      'Northbeam is a marketing attribution and analytics platform for ecommerce brands. Use when the user asks about paid-media performance, blended or per-channel spend/revenue/ROAS/CAC/AOV, which channels or platforms drove revenue, or attribution across models (last touch, first touch, linear, Northbeam custom / clicks + modeled views).\n\nNorthbeam has no synchronous metrics endpoint: performance numbers come from an async data export (create -> poll -> CSV), so the report tools may return status "pending" with an exportId — call northbeam_get-data-export-result with that exportId to retrieve the finished rows.\n\nDiscovery first: call northbeam_list-metrics for metric ids, northbeam_list-attribution-models for model ids, and northbeam_list-breakdowns for breakdown keys. Then:\n- northbeam_summary-metrics: top-line performance metrics over a reporting window. Pass metric ids from list-metrics and either startDate+endDate (YYYY-MM-DD) or a periodType. Start here.\n- northbeam_attribution: metrics broken down (e.g. by \'Platform (Northbeam)\') across one or more attribution models.\n- northbeam_get-data-export-result: fetch a pending export by exportId.\n\nCommon phrases: "Northbeam", "blended ROAS", "MER", "ad spend", "CAC", "attribution", "which channel drove sales", "paid media performance". All tools are read-only.',
    tools: {
      attribution: {},
      get_data_export_result: {},
      list_attribution_models: {},
      list_breakdowns: {},
      list_metrics: {},
      summary_metrics: {},
    },
  },
  notion: {
    key: "notion",
    name: "Notion",
    description:
      "Notion is a connected workspace for notes, docs, wikis, and databases. Search, create, and update pages, query and update databases, add comments, and retrieve workspace content.",
    logoUrl: "/images/integration/logos/notion.svg",
    image: "/images/integration/logos/notion.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "notion",
    useNangoMcp: true,
    usageGuidance:
      'Notion is a connected workspace for notes, docs, wikis, and databases. Use when the user wants to search for Notion pages or content in Notion, create or update Notion pages, query or update Notion databases, add comments, or retrieve information from their Notion workspace. Common phrases: "find in Notion", "create a Notion page", "add to Notion", "update Notion", "Notion database", "search Notion". DO NOT use this integration when the user does not explicitly ask to use Notion. Load the notion skill for detailed tool guidance.',
    tools: {
      append_block_children: { requiresPermission: true },
      append_bulleted_list: { requiresPermission: true },
      append_callout_block: { requiresPermission: true },
      append_code_block: { requiresPermission: true },
      append_divider: { requiresPermission: true },
      append_heading_block: { requiresPermission: true },
      append_todo_block: { requiresPermission: true },
      archive_page: { requiresPermission: true },
      create_comment: { requiresPermission: true },
      create_data_source: { requiresPermission: true },
      create_database: { requiresPermission: true },
      create_database_row: { requiresPermission: true },
      create_page: { requiresPermission: true },
      delete_block: { requiresPermission: true },
      duplicate_page: { requiresPermission: true },
      fetch_content_metadata: {},
      get_bot_user: {},
      get_page_as_markdown: {},
      get_page_property_item: {},
      get_user: {},
      list_block_children: {},
      list_comments: {},
      list_data_source_templates: {},
      list_users: {},
      move_page: { requiresPermission: true },
      query_data_source: {},
      query_database: {},
      query_database_filtered: {},
      query_database_sorted: {},
      restore_page: { requiresPermission: true },
      retrieve_block: {},
      retrieve_block_children: {},
      retrieve_comment: {},
      retrieve_data_source: {},
      retrieve_database: {},
      retrieve_page: {},
      retrieve_page_property: {},
      retrieve_user: {},
      search: {},
      search_databases: {},
      search_pages: {},
      update_block: { requiresPermission: true },
      update_data_source: { requiresPermission: true },
      update_database: { requiresPermission: true },
      update_page: { requiresPermission: true },
      update_page_markdown: { requiresPermission: true },
    },
  },
  okendo: {
    key: "okendo",
    name: "Okendo",
    description:
      "Okendo is a customer marketing platform for Shopify spanning reviews, loyalty, referrals, quizzes, and surveys. Access product reviews, ratings, and auto-generated review summaries, and render reviews and star ratings on pages.",
    logoUrl: "/images/integration/logos/okendo.svg",
    image: "/images/integration/logos/okendo.svg",
    docsUrl: "https://docs.okendo.io/merchant-rest-api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Okendo is a customer marketing platform for Shopify (reviews, loyalty, referrals, quizzes, and surveys); this integration covers its reviews and ratings data. Use these tools when the user asks about product reviews, ratings, star ratings, customer feedback, or review summaries.\n\nAvailable tools:\n- okendo_reviews.list: List all store reviews with optional status filter (approved/pending/rejected) and sorting.\n- okendo_reviews.get: Get a single review by its UUID.\n- okendo_reviews.get_product_reviews: Get published reviews for a specific Shopify product by numeric product ID.\n- okendo_reviews.get_product_aggregate: Get aggregate review stats (average rating, count, star distribution) for a product.\n- okendo_reviews.get_product_summary: Get the AI-generated review summary for a product.\n\nWhen the user wants to display reviews or star ratings on a page, load the "okendo" skill for the correct loader component pattern.\n\nExample triggers: "show me reviews for this product", "what\'s the average rating", "add a reviews section to the page", "feature this review"',
    tools: {
      reviews_get: {},
      reviews_get_product_aggregate: {},
      reviews_get_product_reviews: {},
      reviews_get_product_summary: {},
      reviews_list: {},
    },
  },
  opus: {
    key: "opus",
    name: "OpusClip",
    description:
      "OpusClip is a video repurposing tool that turns long-form videos into short clips. Create clips from a video URL, organize them into collections, and publish or schedule them to social media.",
    logoUrl: "/images/integration/logos/opus.png",
    image: "/images/integration/logos/opus.png",
    docsUrl: "https://help.opus.pro/api-reference/overview#accessing-the-api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'OpusClip is an AI video repurposing tool that turns long-form videos into short, social-ready clips. Use when the user wants to create short video clips from a long-form video (YouTube, Vimeo, etc.), browse or organize clips into collections, or post clips to social media. Common phrases: "clip this video", "make shorts from", "create clips", "post to TikTok/YouTube/Instagram", "add to collection", "show my collections", "generate social copy". IMPORTANT: You MUST load the opusclip-integration skill before using any OpusClip tools. The skill contains mandatory confirmation workflows for project creation and social posting that must be followed. NEVER call projects.create, social.publish, or social.schedule without following the confirmation workflow described in the skill.',
    tools: {
      clips_get: {},
      clips_search: {},
      collections_add_clip: { requiresPermission: true },
      collections_create: { requiresPermission: true },
      collections_delete: { requiresPermission: true },
      collections_get_clips: {},
      collections_list: {},
      collections_remove_clip: { requiresPermission: true },
      projects_create: { requiresPermission: true },
      social_generate_copy: {},
      social_get_accounts: {},
      social_get_copy_result: {},
      social_publish: { requiresPermission: true },
      social_schedule: { requiresPermission: true },
    },
  },
  ordergroove: {
    key: "ordergroove",
    name: "Ordergroove",
    description:
      "Ordergroove is a subscription commerce platform. Read customers, subscriptions, recurring orders, and subscription product records.",
    logoUrl: "/images/integration/logos/ordergroove.svg",
    image: "/images/integration/logos/ordergroove.svg",
    docsUrl: "https://developer.ordergroove.com/reference/authentication",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      "Use Ordergroove when the user asks about subscribers, active or cancelled subscriptions, recurring orders, or Ordergroove subscription product records. Available read-only tools: customers.list, customers.get, subscriptions.list, subscriptions.get, orders.list, and products.list. List tools use cursor pagination. Use Shopify product data and selling plans when building storefront pages. Connecting requires an Ordergroove Application API key with Bulk Operations permission because list tools read across customers.",
    tools: {
      customers_get: {},
      customers_list: {},
      orders_list: {},
      products_list: {},
      subscriptions_get: {},
      subscriptions_list: {},
    },
  },
  parallel: {
    key: "parallel",
    name: "Parallel",
    description:
      "Parallel is a web research API for real-time information. Run ranked web search, extract clean content from URLs, and launch async deep-research tasks with citations.",
    logoUrl: "/images/integration/logos/parallel.png",
    image: "/images/integration/logos/parallel.png",
    docsUrl: "https://docs.parallel.ai/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Parallel is an AI-powered web research API for retrieving current information beyond the model\'s training data. Use Parallel to get current web information the model can\'t know from training data.\n- parallel.search: web search returning ranked URLs with focused excerpts. Triggers: "research X", "find current info on", "what\'s the latest on", "look up competitor". Provide an objective (full sentence) plus 2-3 short keyword queries.\n- parallel.extract: pull clean content/excerpts from specific URLs. Triggers: "extract content from this URL", "read this page". Use after parallel.search to go deeper on the most relevant results.\n- parallel.deepResearch.create: start a long-running, billed deep-research run. ALWAYS confirm with the user before every call (it costs credits and can take minutes). Returns a runId. Does not dedupe.\n- parallel.deepResearch.get: poll a runId; while running it returns status only, and once completed it returns the research output plus citations.',
    tools: {
      deep_research_create: { requiresPermission: true },
      deep_research_get: {},
      extract: {},
      search: {},
    },
  },
  pinterest: {
    key: "pinterest",
    name: "Pinterest",
    description:
      "Pinterest is a visual discovery and social platform. Browse boards and pins, view account analytics, and create pins on your boards to promote Shopify products.",
    logoUrl: "/images/integration/logos/pinterest.svg",
    image: "/images/integration/logos/pinterest.svg",
    docsUrl: "https://developers.pinterest.com/docs/api/v5/",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "pinterest",
    useNangoMcp: true,
    usageGuidance:
      'Pinterest is a visual discovery platform where users save and browse ideas as pins on boards. Use when the user wants to interact with their Pinterest account: browse boards and pins, grab pin image URLs for design inspiration, publish new pins linking to Shopify product pages, or review Pinterest analytics. Common phrases: "Pinterest", "my Pinterest boards", "create a pin", "pin this product", "Pinterest analytics", "Pinterest impressions". Discovery flow: call `get-account` to confirm the connected account, `list-boards` to find a boardId, then `list-pins-on-board` / `get-pin` to browse pins, or `create-pin` to publish. Read tools: `get-account`, `list-boards`, `get-board`, `list-pins-on-board`, `list-pins`, `get-pin`, `get-user-analytics`. Write tool: `create-pin` (always confirm with the user, including imageUrl, title, description, link, and target boardId, before calling).',
    tools: {
      create_pin: { requiresPermission: true },
      get_account: {},
      get_board: {},
      get_pin: {},
      get_user_analytics: {},
      list_boards: {},
      list_pins: {},
      list_pins_on_board: {},
    },
  },
  posthog: {
    key: "posthog",
    name: "PostHog",
    description:
      "PostHog is an open-source product analytics platform. Read-only access via HogQL queries for event counts, top pages, active users, funnels, and conversion analysis.",
    logoUrl: "/images/integration/logos/posthog.svg",
    image: "/images/integration/logos/posthog.svg",
    docsUrl: null,
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "posthog",
    useNangoMcp: true,
    usageGuidance:
      'PostHog is an open-source product analytics platform queried with HogQL (SQL for events). Use when the user wants PostHog analytics data such as event counts, top pages, active users, funnels, or conversion analysis. First call the posthog_list-organizations tool to find a projectId, then call posthog_run-query with a bounded HogQL query. Common phrases: "PostHog", "product analytics", "event counts", "top pages", "active users", "funnel", "conversion rate". Read-only. Load the posthog skill for call order and query examples.',
    tools: {
      list_organizations: {},
      run_query: {},
    },
  },
  postscript: {
    key: "postscript",
    name: "Postscript",
    description:
      "Postscript is an SMS marketing platform for Shopify brands. List and manage subscribers, view opt-in keywords, create custom events to trigger Flows, and update subscriber tags and properties.",
    logoUrl: "/images/integration/logos/postscript.png",
    image: "/images/integration/logos/postscript.png",
    docsUrl: "https://developer.postscript.io/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Postscript is an SMS marketing platform built for Shopify brands. Use when the user wants to manage SMS subscribers, view keywords, or trigger automations in Postscript. Common phrases: "show my subscribers", "find subscriber by phone", "list keywords", "tag this subscriber", "trigger a flow", "create an event", "add a subscriber". Tools: postscript_subscribers.list (list/search subscribers by phone, email, or Shopify customer ID; supports pagination), postscript_subscribers.get (get subscriber by Postscript ID), postscript_subscribers.create (create subscriber; use postscript_keywords.list first to find available keyword IDs, then pass keyword text or ID), postscript_subscribers.update (update tags, properties, email), postscript_keywords.list (list all active opt-in keywords for the shop), postscript_events.create (fire custom event to trigger Flows). Phone numbers must be E.164 format (e.g. +13115552368).',
    tools: {
      events_create: { requiresPermission: true },
      keywords_list: {},
      subscribers_create: { requiresPermission: true },
      subscribers_get: {},
      subscribers_list: {},
      subscribers_update: { requiresPermission: true },
    },
  },
  rebuy: {
    key: "rebuy",
    name: "Rebuy",
    description:
      "Rebuy is a personalization and merchandising platform for Shopify. Get product recommendations, upsells and cross-sells, top sellers, trending products, and smart product search.",
    logoUrl: "/images/integration/logos/rebuy.png",
    image: "/images/integration/logos/rebuy.png",
    docsUrl: "https://developers.rebuyengine.com/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Rebuy is an AI personalization and merchandising engine for Shopify, powering recommendations, upsells, and smart product discovery across the customer journey. Use when the user wants product recommendations, upsell suggestions, cross-sell products, top sellers, trending products, or AI-powered product search via Rebuy. Available tools: rebuy_recommendations.get (get AI recommendations for specific products), rebuy_topSellers.get (get best-selling products), rebuy_trendingProducts.get (get trending products), rebuy_search (AI-powered product search). Common phrases: "recommend products", "what sells well", "trending products", "upsell", "cross-sell", "similar products", "best sellers".',
    tools: {
      recommendations_get: {},
      search: {},
      top_sellers_get: {},
      trending_products_get: {},
    },
  },
  recharge: {
    key: "recharge",
    name: "Recharge",
    description:
      "Recharge is a subscriptions and recurring billing platform for ecommerce. View customers, subscriptions, and charge history, and analyze subscription metrics.",
    logoUrl: "/images/integration/logos/recharge.png",
    image: "/images/integration/logos/recharge.png",
    docsUrl: null,
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "recharge",
    useNangoMcp: true,
    usageGuidance:
      'Recharge is a leading subscriptions and recurring billing platform for ecommerce brands. Use when the user asks about recurring subscriptions managed by Recharge, wants to look up a customer\'s subscription status, view charge history, or analyze subscription metrics. Available tools (auto-discovered via Nango MCP): list-subscriptions (search by customer, email, or status), get-subscription (full details by ID), list-charges (charge history with filters), get-customer (customer with subscription details), list-customers (search customers by email). Trigger phrases: "show subscriptions", "Recharge data", "subscription status for [email]", "failed charges", "recurring orders".',
    tools: {
      get_customer: {},
      get_subscription: {},
      list_charges: {},
      list_customers: {},
      list_subscriptions: {},
    },
  },
  "replo-assets": {
    key: "replo-assets",
    name: "Assets",
    description:
      "Find and reuse project images and videos while building pages in Replo.",
    logoUrl: "/images/integration/logos/Replo.svg",
    image: "/images/integration/logos/Replo.svg",
    docsUrl: null,
    connectionUi: null,
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      "Use when the user wants to reference an uploaded image or video asset.",
    tools: {},
  },
  "replo-integrations": {
    key: "replo-integrations",
    name: "Scripts & Pixels",
    description:
      "Reference the marketing and analytics scripts that can be installed on this site, such as Google Analytics, Meta Pixel, and Hotjar.",
    logoUrl: "/images/integration/logos/Replo.svg",
    image: "/images/integration/logos/Replo.svg",
    docsUrl: null,
    connectionUi: null,
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      "Use when the user wants to reference or configure a tracking script or pixel on their site. This is not the connected-integrations catalog — for Shopify, Klaviyo, PostHog and other connected tools, use that integration's own tools.",
    tools: {
      scripts_search: {},
    },
  },
  "replo-products": {
    key: "replo-products",
    name: "Replo Products",
    description:
      "Create, manage, and display Replo products with Stripe checkout built into the page flow.",
    logoUrl: "/images/integration/logos/Replo.svg",
    image: "/images/integration/logos/Replo.svg",
    docsUrl: null,
    connectionUi: null,
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Use for displaying products, building product pages, creating product listings, or managing products (search, create, update, restructure, duplicate, delete). Start with products.search to find a product\'s UUID — every other tool takes one. Price and variants live on products.setVariants, photos on products.setMedia. For checkout, Stripe Connect must be configured on the project. ALSO use for print-on-demand merch: Replo natively makes and ships real merchandise (t-shirts, hoodies, mugs) through fulfillment providers like Printful — paid orders are printed and shipped automatically, with no manual fulfillment. Browse garments with catalog.search / catalog.get, import one with products.createFromCatalog, put artwork on it with products.placeDesign, and render photorealistic previews with products.renderMockups / products.saveMockups. NEVER tell a user that Printful or print-on-demand is unsupported or that fulfillment is manual. Common phrases: "show products", "what do I sell", "product page", "add a product", "product grid", "product detail", "featured products", "create a product", "update product", "delete product", "change product price", "add a size", "add a color", "duplicate this product", "make a t-shirt", "merch", "print on demand", "printful", "put this design on a shirt". IMPORTANT: You MUST load the replo-products skill before using any product tools or writing page code that involves product data. The skill contains the mandatory Product Mutation Workflow (impact analysis, user confirmation, and page updates) that must be followed for all create/update/delete operations. NEVER call a mutating product tool without following this workflow.',
    tools: {
      catalog_get: {},
      catalog_search: {},
      products_create: { requiresPermission: true },
      products_create_from_catalog: { requiresPermission: true },
      products_delete: { requiresPermission: true },
      products_duplicate: { requiresPermission: true },
      products_get: {},
      products_place_design: { requiresPermission: true },
      products_render_mockups: { requiresPermission: true },
      products_save_mockups: { requiresPermission: true },
      products_search: {},
      products_set_media: { requiresPermission: true },
      products_set_variants: { requiresPermission: true },
      products_update: { requiresPermission: true },
    },
  },
  "reviews-io": {
    key: "reviews-io",
    name: "Reviews.io",
    description:
      "Reviews.io is a product and company review platform for ecommerce. This integration reads product reviews, star-rating aggregates, and merchant/company reviews, and renders reviews and ratings on pages.",
    logoUrl: "/images/integration/logos/reviews-io.png",
    image: "/images/integration/logos/reviews-io.png",
    docsUrl: "https://developer.reviews.io/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Reviews.io covers product and company review data for ecommerce storefronts. Use when the user asks about Reviews.io product reviews, star ratings, review counts, or company/merchant reviews. Common phrases: "show Reviews.io reviews", "what\'s the average rating", "how many reviews does this product have", "company reviews".\n\nAvailable tools:\n- reviews-io_reviews.get_product_reviews: List published product reviews by Reviews.io product key (`sku` param — usually merchant SKU or Shopify variant ID). Paginated.\n- reviews-io_reviews.get_product_aggregate: Get aggregate stats (average score, total review count) for a product key.\n- reviews-io_reviews.list_merchant: List company/merchant reviews for the connected store.\n\nProduct keying is critical: Reviews.io keys reviews by an opaque `sku` string written by the store\'s catalog sync — often merchant SKU or Shopify variant ID, not Shopify product ID. Resolve and confirm the key via tools (see the reviews-io skill) and persist the scheme to project memory. Do not pass a Shopify product ID expecting server-side expansion.\n\nWhen the user wants to display reviews or star ratings on a page, load the "reviews-io" skill for the correct loader component pattern.\n\nDO NOT use this integration when the user does not explicitly ask about reviews/ratings or Reviews.io.',
    tools: {
      reviews_get_product_aggregate: {},
      reviews_get_product_reviews: {},
      reviews_list_merchant: {},
    },
  },
  segment: {
    key: "segment",
    name: "Segment",
    description:
      "Segment is a customer data platform (CDP) that collects and routes event data across tools. Read-only access to sources and their write keys, connected destinations, and tracking plans.",
    logoUrl: "/images/integration/logos/segment.svg",
    image: "/images/integration/logos/segment.svg",
    docsUrl: "https://docs.segmentapis.com/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Segment is a customer data platform (CDP) that collects first-party event data and routes it to downstream destinations. Use when the user wants to understand their Segment analytics setup, find a source\'s write key, list destinations connected to a source, or inspect tracking plan event schemas. Tool names: sources.list, sources.get, destinations.list, destinations.get, trackingPlans.list, trackingPlans.get. Common phrases: "my Segment sources", "what destinations do I have", "find the write key for my website source", "show my tracking plan", "what events are tracked". This integration is read-only.',
    tools: {
      destinations_get: {},
      destinations_list: {},
      sources_get: {},
      sources_list: {},
      tracking_plans_get: {},
      tracking_plans_list: {},
    },
  },
  semrush: {
    key: "semrush",
    name: "Semrush",
    description:
      "Semrush is an SEO, SEM, and AEO intelligence platform. Pull domain overview, keyword metrics, related keywords, a domain's organic keywords, and backlink data to guide organic search and answer-engine visibility.",
    logoUrl: "/images/integration/logos/semrush.svg",
    image: "/images/integration/logos/semrush.svg",
    docsUrl: "https://developer.semrush.com/api/v3/get-started/api-access/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Semrush is an SEO/SEM competitive intelligence suite that now also covers AI visibility and AEO (answer engine optimization). Use these tools for SEO/SEM research: keyword volume and difficulty, competitor domain analysis, related keyword ideas, and backlink profiles.\n\nIMPORTANT: The five data tools (semrush.domainOverview, semrush.keywordOverview, semrush.relatedKeywords, semrush.domainOrganicKeywords, semrush.backlinksOverview) each consume paid Semrush API units, so they require user approval before running. Use semrush.apiUnitsBalance (free, no approval) to check the remaining unit budget before running costed tools or when the user asks about their Semrush usage.\n\nExample triggers: "what\'s the search volume for X", "how hard is it to rank for X", "what keywords does competitor.com rank for", "domain authority/overview for X", "show backlinks for X", "how many Semrush API units do I have left".',
    tools: {
      semrush_api_units_balance: {},
      semrush_backlinks_overview: { requiresPermission: true },
      semrush_domain_organic_keywords: { requiresPermission: true },
      semrush_domain_overview: { requiresPermission: true },
      semrush_keyword_overview: { requiresPermission: true },
      semrush_related_keywords: { requiresPermission: true },
    },
  },
  shopify: {
    key: "shopify",
    name: "Shopify",
    description:
      "Connect Shopify to bring products and collections into Replo.",
    logoUrl: "/images/integration-logos/shopify.png",
    image: "/images/integration-logos/shopify.png",
    docsUrl: null,
    connectionUi: "shopify-app-install",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Shopify is the ecommerce platform powering the connected storefront and product catalog. Use when the user wants to display Shopify products, collections, or product listings on a page, search for products or collections, build product detail pages, create collection grids, or render any Shopify storefront data. Common phrases: "show products", "product page", "collection page", "add a product", "display collection", "product grid", "product detail", "featured products", "product list". IMPORTANT: You must ALWAYS load the shopify skill before writing any page code that involves Shopify data, since it contains the required data loading pattern (prefetch + loader components) and code examples that must be followed exactly.',
    tools: {
      collections_get: {},
      collections_search: {},
      discount_codes_get: {},
      discount_codes_search: {},
      domains_list: {},
      metafield_definitions_list: {},
      metafields_get: {},
      metaobject_definitions_list: {},
      metaobjects_get: {},
      metaobjects_list: {},
      products_get: {},
      products_search: {},
    },
  },
  skio: {
    key: "skio",
    name: "Skio",
    description:
      "Skio is a subscriptions platform for Shopify brands. View, search, and analyze customer subscriptions and recent subscription orders.",
    logoUrl: "/images/integration/logos/skio.png",
    image: "/images/integration/logos/skio.png",
    docsUrl: null,
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "skio",
    useNangoMcp: true,
    usageGuidance:
      'Skio is a subscriptions platform for Shopify brands, focused on recurring revenue and subscriber management. Use when the user asks about subscriptions managed by Skio, wants to look up a customer\'s subscription status, or needs subscription analytics. Available tools (auto-discovered via Nango MCP): list-subscriptions (search by email or status), get-subscription (get full details by ID), list-orders (recent subscription orders). Trigger phrases: "show subscriptions", "find subscription for [email]", "subscription status", "Skio data".',
    tools: {
      get_subscription: {},
      list_orders: {},
      list_subscriptions: {},
    },
  },
  slack: {
    key: "slack",
    name: "Slack",
    description:
      "Slack is a team messaging and collaboration platform. Read public channels, inspect threads, search messages and users, manage reactions, send messages, and set thread assistant status.",
    logoUrl: "/images/integration/logos/slack.svg",
    image: "/images/integration/logos/slack.svg",
    docsUrl: "https://nango.dev/docs/api-integrations/slack",
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "slack",
    useNangoMcp: true,
    usageGuidance:
      'Slack is a team messaging and collaboration platform organized into channels and threads. Use when the user wants to read public Slack channels, inspect public-channel thread history, search public channels, messages, or users, add/remove/get message reactions, send messages, set thread assistant status, or look up Slack users. Prefer the hydrated Slack runtime context in the prompt for the current team, channel, thread, and requester; do not re-ask the user for IDs that are already provided there. Prefer search_channels over list_channels when finding a public destination by name or topic. Use search_messages for workspace-wide public-channel message search. Use search_users when finding people by name, title, or email. When sending from an existing Slack thread, set send_message.channel to the hydrated channel and send_message.thread_ts to the hydrated thread timestamp by default so the message stays in context; omit thread_ts only when the user explicitly asks for a new top-level channel message or DM. When the user asks to upload or attach files, images, or other assets into Slack, use send_message.assets so they appear as Slack-uploaded files instead of pasted URLs. For every Slack-originated prompt, the first assistant tool-call message must load the Slack skill and include set_status with the hydrated channel and thread_ts, including for short tasks and routine reads. Put the skill load first and set_status immediately below it in the same message; do not wait for the skill result before calling status. Status must start with "is " (e.g. "is juggling...") so Slack renders "Replo is juggling...". Update it when the subtask changes, but never clear it yourself; final turn delivery clears it.',
    tools: {
      add_reaction: { requiresPermission: true },
      get_channel_info: {},
      get_channel_members: {},
      get_conversation_history: {},
      get_message_permalink: {},
      get_reactions: {},
      get_team_info: {},
      get_thread_replies: {},
      get_user_info: {},
      get_user_profile: {},
      join_channel: { requiresPermission: true },
      list_channels: {},
      list_files: {},
      list_users: {},
      lookup_user_by_email: {},
      remove_reaction: { requiresPermission: true },
      search_channels: {},
      search_messages: {},
      search_users: {},
      send_message: { requiresPermission: true },
      set_status: {},
    },
  },
  "smile-io": {
    key: "smile-io",
    name: "Smile.io",
    description:
      "Smile.io is a loyalty, rewards, and referral platform for ecommerce. Read-only access to earning rules, VIP tiers, rewards catalog, customer points balances, and points transaction history.",
    logoUrl: "/images/integration/logos/smile-io.svg",
    image: "/images/integration/logos/smile-io.svg",
    docsUrl: "https://dev.smile.io/api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Smile.io is a loyalty, rewards, and referral platform for ecommerce brands (points, VIP tiers, and rewards). Use when the user wants to view or display loyalty program data: VIP tiers, earning rules, rewards catalog, customer points balances, or points transaction history. Common phrases: "loyalty program", "points balance", "VIP tier", "earning rules", "rewards catalog", "how do customers earn points", "loyalty landing page", "points transactions". Tools: loyalty.earningRules, loyalty.vipTiers, loyalty.rewards, loyalty.customers, loyalty.customer, loyalty.pointsTransactions. Load the smile-io-integration skill before building loyalty landing pages.',
    tools: {
      loyalty_customer: {},
      loyalty_customers: {},
      loyalty_earning_rules: {},
      loyalty_points_transactions: {},
      loyalty_rewards: {},
      loyalty_vip_tiers: {},
    },
  },
  "snapchat-ads": {
    key: "snapchat-ads",
    name: "Snapchat Ads",
    description:
      "Snapchat Ads is Snap's advertising platform. Read organizations, ad accounts, campaigns, ad squads, and ads, pull performance stats, and pause or resume campaigns.",
    logoUrl: "/images/integration/logos/snapchat.svg",
    image: "/images/integration/logos/snapchat.svg",
    docsUrl: null,
    connectionUi: "nango-oauth",
    nangoProviderConfigKey: "snapchat",
    useNangoMcp: true,
    usageGuidance:
      'Snapchat Ads is Snap\'s advertising platform for reaching audiences with image and video ads. Use when the user wants to inspect or manage their Snapchat Ads account: list organizations and ad accounts, view campaigns/ad squads/ads, pull performance metrics (impressions, swipes, spend, conversions), or pause/resume campaigns. Common phrases: "Snapchat Ads", "Snap Ads", "my Snap campaigns", "Snapchat ad spend", "Snapchat performance", "pause Snap campaign", "resume Snap campaign". Discovery flow: call `list-organizations` first to find an organizationId, then `list-ad-accounts` with that organizationId, then `list-campaigns` / `list-ad-squads` / `list-ads` with the chosen adAccountId. Read tools: `list-organizations`, `list-ad-accounts`, `list-campaigns`, `list-ad-squads`, `list-ads`, `get-campaign-stats`. Mutate tool: `update-campaign-status` (ACTIVE or PAUSED; always confirm with the user before calling).',
    tools: {
      get_campaign_stats: {},
      list_ad_accounts: {},
      list_ad_squads: {},
      list_ads: {},
      list_campaigns: {},
      list_organizations: {},
      update_campaign_status: { requiresPermission: true },
    },
  },
  stamped: {
    key: "stamped",
    name: "Stamped.io",
    description:
      "Stamped.io is a reviews, ratings, and loyalty platform for ecommerce. Access product reviews, star ratings, and customer feedback, including admin moderation data.",
    logoUrl: "/images/integration/logos/stamped.png",
    image: "/images/integration/logos/stamped.png",
    docsUrl: "https://developers.stamped.io/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Stamped.io is a reviews, ratings, and loyalty platform for ecommerce brands. Use when the user asks about product reviews, star ratings, review counts, or customer feedback from Stamped.io. Common phrases: "show reviews for this product", "what\'s the average rating", "how many reviews does this product have", "show recent reviews", "find reviews by customer", "show me the review dashboard". Tools: reviews.list (list public reviews with optional filtering by productId, productSku, or email), reviews.get (get a single review by ID), dashboardReviews.list (list reviews with admin-level metadata like moderation status).',
    tools: {
      dashboard_reviews_list: {},
      reviews_get: {},
      reviews_list: {},
    },
  },
  statsig: {
    key: "statsig",
    name: "Statsig",
    description:
      "Statsig is a unified feature flag, experimentation, and product analytics platform. Read and manage feature gates and experiments, read Pulse (experiment) results, and run server-side experiment cohorting so pages can render the assigned variant during SSR.",
    logoUrl: "/images/integration/logos/statsig.svg",
    image: "/images/integration/logos/statsig.svg",
    docsUrl: "https://console.statsig.com/api_keys",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Statsig is a feature-flag, A/B testing, and product-analytics platform. Use when the user wants to review or manage Statsig feature gates and experiments or read experiment results. Available tools: statsig_gates.list / statsig_gates.get (feature gates), statsig_experiments.list / statsig_experiments.get (experiments), statsig_experiments.getPulseResults (per-variant Pulse results: metric lift and significance — requires control and test group IDs from experiments.get), statsig_gates.create / statsig_gates.update (create or enable/disable a gate), statsig_experiments.create (create an experiment shell), statsig_experiments.start (move an experiment to running). List tools paginate with page (1-indexed), not offset. Common phrases: "list my Statsig experiments", "how is my experiment doing", "what\'s the winning variant / lift", "create a feature gate", "roll out this gate", "start my experiment". IMPORTANT: Always confirm with the user before calling any create/update/start tool, since these change live Statsig configuration. experiments.create / gates.create only create shells (name/description) — groups, parameters, allocation, and targeting rules must be configured in the Statsig Console before calling experiments.start or running an on-page experiment. To run an experiment on a published page, use the statsig.experiment data loader for SSR variant cohorting.',
    tools: {
      experiments_create: { requiresPermission: true },
      experiments_get: {},
      experiments_get_pulse_results: {},
      experiments_list: {},
      experiments_start: { requiresPermission: true },
      gates_create: { requiresPermission: true },
      gates_get: {},
      gates_list: {},
      gates_update: { requiresPermission: true },
    },
  },
  "stay-ai": {
    key: "stay-ai",
    name: "Stay.ai",
    description:
      "Stay.ai is a subscription and retention platform for Shopify brands. View, search, and analyze customer subscriptions and orders, and generate customer portal links.",
    logoUrl: "/images/integration/logos/stay-ai.png",
    image: "/images/integration/logos/stay-ai.png",
    docsUrl: "https://docs.stay.ai/reference",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Stay.ai is a subscription and retention platform for Shopify brands, focused on growing and retaining subscribers. Use when the user asks about subscriptions managed by Stay.ai (formerly Retextion/STAY), wants to look up a customer\'s subscription status, view subscription history, or generate a customer portal link. Available tools: subscriptions.list (search by email or status with pagination), subscriptions.get (full details by Shopify GID or internal ID), subscriptions.listOrders (order history), subscriptions.generatePortalLink (generate customer self-service portal URL). Trigger phrases: "show subscriptions", "Stay subscriptions", "subscription status for [email]", "generate portal link".',
    tools: {
      subscriptions_generate_portal_link: {},
      subscriptions_get: {},
      subscriptions_list: {},
      subscriptions_list_orders: {},
    },
  },
  stripe: {
    key: "stripe",
    name: "Stripe",
    description:
      "Stripe is a payments and financial infrastructure platform. Access orders and payments from connected Stripe accounts, including transactions, payment intents, payment status, order history, and account balance.",
    logoUrl: "/images/integration/logos/stripe.svg",
    image: "/images/integration/logos/stripe.svg",
    docsUrl: null,
    connectionUi: null,
    nangoProviderConfigKey: "stripe-api-key",
    useNangoMcp: true,
    usageGuidance:
      'Stripe is a payments and financial infrastructure platform for accepting and managing online payments. Use when the user wants to view orders or payments from their Stripe Connect account, list recent transactions or payment intents, check payment status or order details, get order history for a customer, or check account balance or payout information. Common phrases: "show my orders", "list payments", "Stripe orders", "recent transactions", "payment history", "check order status", "account balance". Load the stripe skill for detailed tool guidance.',
    tools: {
      get_balance: {},
      get_order: {},
      get_payment: {},
      list_customers: {},
      list_orders: {},
      list_payments: {},
    },
  },
  trendtrack: {
    key: "trendtrack",
    name: "TrendTrack",
    description:
      "TrendTrack is an ecommerce intelligence platform. Search Shopify shops, find winning products, scan Meta ads, and pull brandtracker overviews and top ads.",
    logoUrl: "/images/integration/logos/trendtrack.svg",
    image: "/images/integration/logos/trendtrack.svg",
    docsUrl: "https://docs.trendtrack.io/connect/api",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'TrendTrack is a competitive ecommerce intelligence suite (shops, products, ads, tracked brands). Use it when the user wants niche winners, lookalike stores, Meta ad breakdowns, or updates on brands they already track in TrendTrack.\n\nIMPORTANT: Most TrendTrack data tools consume paid credits. Always confirm before metered calls, or check remaining credits with trendtrack.checkCredits first (free / unmetered). Prefer the brandtracker path for tracked brands: listBrandtrackers → brandtrackerOverview → brandtrackerTopAds.\n\nExample triggers: "what\'s winning in pet supplies", "find shops like glossier.com", "scan this Meta ad", "brief my tracked brand", "TrendTrack credits left".',
    tools: {
      trendtrack_brandtracker_overview: { requiresPermission: true },
      trendtrack_brandtracker_top_ads: { requiresPermission: true },
      trendtrack_check_credits: {},
      trendtrack_get_ad: { requiresPermission: true },
      trendtrack_list_brandtrackers: {},
      trendtrack_search_shops: { requiresPermission: true },
      trendtrack_winning_products: { requiresPermission: true },
    },
  },
  "triple-whale": {
    key: "triple-whale",
    name: "Triple Whale",
    description:
      "Triple Whale is an ecommerce analytics and attribution platform for Shopify brands. Read Summary Page metrics (blended and per-channel spend, revenue, ROAS, MER, orders), full customer-journey attribution, and run custom SQL against the Triple Whale data warehouse. Also supports submitting GDPR/CCPA customer-data deletion requests.",
    logoUrl: "/images/integration/logos/triplewhale.svg",
    image: "/images/integration/logos/triplewhale.svg",
    docsUrl: "https://developers.triplewhale.com/",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'Triple Whale is an ecommerce analytics and attribution platform for Shopify brands. Use when the user asks about store performance, ad spend, revenue, ROAS, MER, blended metrics, which channels/campaigns drove orders, or any custom analytics question about their Triple Whale data. The shop domain is configured on the connection, so no tool takes a shop argument; all dates are YYYY-MM-DD. Tools: summaryMetrics (top-line KPIs — start here), orderAttribution (per-order customer-journey attribution), customSqlQuery (read-only ClickHouse SQL against the data warehouse), complianceDeletionRequest (MUTATING GDPR/CCPA deletion — confirm identifiers first). Common phrases: "blended ROAS", "MER", "ad spend", "attribution", "customer journey", "run this query", "delete this customer\'s data". Load the triple-whale skill before calling any Triple Whale tool — it covers tool selection, Replo analytics views, warehouse schema, and SQL conventions.',
    tools: {
      compliance_deletion_request: { requiresPermission: true },
      custom_sql_query: {},
      order_attribution: {},
      summary_metrics: {},
    },
  },
  vwo: {
    key: "vwo",
    name: "VWO",
    description:
      "VWO (Visual Website Optimizer) is an A/B testing and experimentation platform. List A/B test campaigns and read experiment results like visitors, conversions, conversion rate, and lift.",
    logoUrl: "/images/integration/logos/vwo.png",
    image: "/images/integration/logos/vwo.png",
    docsUrl: "https://developers.vwo.com/reference/introduction-1",
    connectionUi: "custom-api-key",
    nangoProviderConfigKey: null,
    useNangoMcp: false,
    usageGuidance:
      'VWO (Visual Website Optimizer) is a web experimentation and conversion optimization platform. Use when the user wants to review VWO A/B tests or experiment results. Available tools: vwo_campaigns.list (list A/B test campaigns), vwo_campaigns.get (full configuration and summary stats for one campaign), vwo_campaigns.getReport (per-variation results: visitors, conversions, conversion rate, lift, chance-to-beat). Common phrases: "list my VWO experiments", "how is my VWO homepage test doing", "what\'s the winning variation", "show VWO conversion rates". Read-only: this integration cannot create, start, pause, or stop campaigns.',
    tools: {
      campaigns_get: {},
      campaigns_get_report: {},
      campaigns_list: {},
    },
  },
  yotpo: {
    key: "yotpo",
    name: "Yotpo",
    description:
      "Yotpo is a retention marketing platform for ecommerce spanning reviews, loyalty, and subscriptions. This integration reads product reviews, star ratings, and aggregate review stats, and renders reviews and star ratings on pages.",
    logoUrl: "/images/integration/logos/yotpo.png",
    image: "/images/integration/logos/yotpo.png",
    docsUrl: "https://apidocs.yotpo.com/reference/welcome",
    connectionUi: "nango-connect-ui",
    nangoProviderConfigKey: "yotpo",
    useNangoMcp: true,
    usageGuidance:
      'Yotpo is a retention marketing platform for ecommerce (reviews, loyalty, and subscriptions); this integration covers its product reviews and ratings data. Use when the user asks about Yotpo product reviews, star ratings, review counts, or aggregate review data. Common phrases: "show Yotpo reviews", "what\'s the average rating", "how many reviews does this product have", "list reviews for this product", "Yotpo bottomline".\n\nAvailable tools:\n- yotpo_reviews.list: List reviews from the merchant\'s Yotpo store with optional filters (productId, score, sentiment, status). Paginated.\n- yotpo_reviews.get: Get a single review by ID.\n- yotpo_products.bottomline: Get aggregate stats (average score, total review count, star distribution) for a Shopify product.\n\nWhen the user wants to display reviews or star ratings on a page, load the "yotpo" skill for the correct loader component pattern.\n\nDO NOT use this integration when the user does not explicitly ask about reviews/ratings or Yotpo.',
    tools: {
      get_product_bottomline: {},
      get_review: {},
      list_reviews: {},
    },
  },
} as const satisfies Record<KnownIntegrationKey, KnownIntegrationMetadata>;
